import {
    CallMissedOutgoing,
    Troubleshoot,
    Visibility,
} from "@mui/icons-material";
import {
    Box,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Tab,
    Tabs,
} from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { PageDefault } from "../../components/PageDefault";
import PageWrapper from "../../components/PageWrapper";
import TableDataGrid from "../../components/TableDataGrid";
import { defaultToast } from "../../helpers/toast/defaultToast";
import { api } from "../../services/api";
import { FilterHeader } from "../CustomersPage/FilterHeader";
import { applyCustomerFilters } from "./applyFilters";
import { CustomerStatus as CustomerStatusComponent } from "./CustomerStatus";
import { financialStatusLabel } from "./functions";
import { projeto704InfraColumns } from "./GridColumns";
import { ModalCustomerCentral } from "./Modal";
import {
    Content,
    Title,
    TableContent,
    InfraCustomers,
    CustomerAdmin,
} from "./styles";
import {
    CustomerDisagreements,
    CustomerStatus,
    Projeto704InfraFilter,
} from "./types";

const projeto704Filters: Projeto704InfraFilter[] = [
    {
        id: "all",
        label: "Todos Infra704",
    },
    {
        id: "running",
        label: "VM Online",
    },
    {
        id: "offline",
        label: "VM Offline",
    },
    {
        id: "not_provisioned",
        label: "Sem provisionamento",
    },
    {
        id: "divergences",
        label: "Divergências Admin x Projeto704",
    },
];

export function CustomerCentral() {
    const [customerStatus, setCustomerStatus] = useState<CustomerStatus>({
        ok: 0,
        warning: 0,
        blocked: 0,
        in_delete_queue: 0,
        deleted: 0,
        waiting_activate: 0,
        waiting_server: 0,
        validated: 0,
        off: 0,
    });

    const [loading, setLoading] = useState(true);
    const [loadingInfra, setLoadingInfra] = useState(true);
    const [infraData, setInfraData] = useState<Record<string, unknown>[]>([]);
    const [currentFilter, setCurrentFilter] = useState<string>("all");
    const [queryFilter, setQueryFilter] = useState("");
    const [tabIndex, setTabIndex] = useState(0);
    const [openModal, setOpenModal] = useState(false);
    const navigate = useNavigate();
    const [currentCustomer, setCurrentCustomer] =
        useState<CustomerDisagreements>();
    const [disagreements, setDisagreements] = useState<CustomerDisagreements[]>(
        []
    );

    function handleDataInfra(filter = currentFilter) {
        setLoadingInfra(true);
        return api
            .get(`/customerInfra/projeto704/overview?filter=${filter}`)
            .then((response) => {
                setInfraData(response.data.response);
            })
            .catch((error) => {
                defaultToast(
                    "error",
                    error?.response?.data?.message ||
                        "Erro ao carregar overview Projeto704"
                );
                setInfraData([]);
            })
            .finally(() => {
                setLoadingInfra(false);
            });
    }

    function handleChangeFilter(filter: string) {
        setCurrentFilter(filter);
        handleDataInfra(filter);
    }

    function handleCustomerStatus() {
        return api.get("/v2/customer/status").then((response) => {
            setCustomerStatus(response.data.response);
        });
    }

    function handleCustomerDisagreement() {
        return api.get("/v2/customer/disagreement").then((response) => {
            setDisagreements(response.data.response);
        });
    }

    const columnsCustomerDisagreements: GridColDef[] = [
        { field: "name", headerName: "Cliente", flex: 3 },
        {
            field: "status",
            headerName: "Status",
            flex: 1,
            renderCell: (params: GridRenderCellParams) => (
                <strong>{params.row?.status.toUpperCase()}</strong>
            ),
        },
        {
            field: "days_past_due",
            headerName: "Situação financeira",
            flex: 2,
            renderCell: (params: GridRenderCellParams) => (
                <span
                    style={{
                        color: financialStatusLabel(params.row).color,
                    }}
                >
                    {financialStatusLabel(params.row).label}
                </span>
            ),
        },
        {
            field: "disagreements",
            headerName: "Inconsistências",
            flex: 2,
            renderCell: (params: GridRenderCellParams) => (
                <strong>
                    {`${params.row?.disagreements.length} inconsistencias`}
                </strong>
            ),
        },
        {
            field: "actions",
            headerName: "Ações",
            flex: 1,
            align: "right",
            headerAlign: "right",
            renderCell: (params: GridRenderCellParams) => (
                <div
                    style={{
                        display: "flex",
                        gap: "0 6px",
                    }}
                >
                    <CallMissedOutgoing
                        style={{
                            cursor: "pointer",
                        }}
                        onClick={() => {
                            navigate(
                                `/comercial/customers/customerDetail?id=${params.row.id}`
                            );
                        }}
                    />
                    <Visibility
                        style={{
                            cursor: "pointer",
                        }}
                        onClick={() => {
                            setCurrentCustomer(params.row);
                            setOpenModal(true);
                        }}
                    />
                </div>
            ),
        },
    ];

    useEffect(() => {
        Promise.all([
            handleCustomerStatus(),
            handleCustomerDisagreement(),
            handleDataInfra("all"),
        ]).finally(() => {
            setLoading(false);
        });
    }, []);

    const filteredInfraData = applyCustomerFilters(infraData, queryFilter);
    const filteredDisagreements = applyCustomerFilters(
        disagreements,
        queryFilter
    );

    const handleChangeTab = (
        _event: React.SyntheticEvent,
        newValue: number
    ) => {
        setTabIndex(newValue);
    };

    function a11yProps(index: number) {
        return {
            id: `simple-tab-${index}`,
            "aria-controls": `simple-tabpanel-${index}`,
        };
    }

    return (
        <PageWrapper>
            <PageDefault
                headerPage
                header={{
                    title: "Central de clientes",
                    subtitle: "Análise geral de clientes (Projeto704)",
                    icon: <Troubleshoot sx={{ color: "#fff" }} />,
                }}
                heightAuto
            >
                <Content>
                    <Title>Visão geral Clientes</Title>
                    <CustomerStatusComponent customerStatus={customerStatus} />
                    <FilterHeader
                        handleSearch={(query: string) => {
                            setQueryFilter(query);
                        }}
                        hideAddClient
                    />
                    <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                        <Tabs
                            value={tabIndex}
                            onChange={handleChangeTab}
                            aria-label="tab-divergentes"
                        >
                            <Tab label="Infra Projeto704" {...a11yProps(0)} />
                            <Tab
                                label="Divergências no Admin"
                                {...a11yProps(1)}
                            />
                        </Tabs>
                    </Box>
                    {tabIndex === 0 ? (
                        <InfraCustomers>
                            <div className="filter">
                                <FormControl fullWidth size="small">
                                    <InputLabel id="projeto704-filter-label">
                                        Filtrar por status da VM
                                    </InputLabel>
                                    <Select
                                        name="projeto704-filter"
                                        value={currentFilter}
                                        labelId="projeto704-filter-label"
                                        id="projeto704-filter"
                                        label="Filtrar por status da VM"
                                        size="small"
                                        onChange={(event) => {
                                            handleChangeFilter(
                                                String(event.target.value)
                                            );
                                        }}
                                    >
                                        {projeto704Filters.map((option) => (
                                            <MenuItem
                                                value={option.id}
                                                key={option.id}
                                            >
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>
                            <TableContent>
                                <TableDataGrid
                                    hideFooter
                                    disableColumnMenu
                                    rows={filteredInfraData}
                                    columns={projeto704InfraColumns}
                                    loading={loadingInfra}
                                    hideColumnMenu
                                    getRowId={(row) => row.id}
                                />
                            </TableContent>
                        </InfraCustomers>
                    ) : (
                        <CustomerAdmin>
                            <Divider />
                            <TableContent>
                                <TableDataGrid
                                    hideFooter
                                    disableColumnMenu
                                    rows={filteredDisagreements}
                                    columns={columnsCustomerDisagreements}
                                    loading={loading}
                                    hideColumnMenu
                                    hideFilter
                                />
                            </TableContent>
                            <ModalCustomerCentral
                                currentCustomer={currentCustomer}
                                openModal={openModal}
                                setOpenModal={setOpenModal}
                            />
                        </CustomerAdmin>
                    )}
                </Content>
            </PageDefault>
        </PageWrapper>
    );
}
